import { sortContactPoints } from '../../address-book/contactPoints';
import type { ContactPoint } from '../../address-book/contactPoints';

export type ComposeRecipientSuggestionSource = 'recent' | 'addressBook';

export type ComposeRecipientSuggestion = {
  email: string;
  name?: string | null;
  source: ComposeRecipientSuggestionSource;
  lastUsedAt?: string;
};

export type AddressBookSuggestionEntry = {
  email: string;
  name?: string | null;
};

export type RecentRecipientSuggestionInput = {
  email: string;
  name?: string | null;
  lastUsedAt?: string;
};

export type FilteredComposeRecipientSuggestions = {
  recent: ComposeRecipientSuggestion[];
  addressBook: ComposeRecipientSuggestion[];
  flat: ComposeRecipientSuggestion[];
};

const EMPTY_QUERY_RECENT_LIMIT = 8;
const MAX_PER_SECTION = 12;
const MAX_TOTAL = 20;

function isPlausibleEmail(value: string): boolean {
  const t = value.trim();
  return t.length >= 3 && t.includes('@') && !/\s/.test(t);
}

function normalizeEmail(raw: string): string | null {
  const email = raw.trim();
  if (!isPlausibleEmail(email)) return null;
  return email;
}

/** Format a suggestion as a compose pill token (`Name <email>` when named). */
export function formatComposeRecipientToken(name: string | null | undefined, email: string): string {
  const trimmedEmail = email.trim();
  const displayName = name?.trim();
  if (displayName && !displayName.includes('@')) {
    const safe = displayName.replace(/"/g, '\\"');
    return `${safe} <${trimmedEmail}>`;
  }
  return trimmedEmail;
}

function pickDisplayName(...candidates: Array<string | null | undefined>): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed && !trimmed.includes('@')) return trimmed;
  }
  return undefined;
}

/** Collect unique address-book emails with best-effort display names. */
export function collectAddressBookSuggestions(
  organizations: Array<{ clientName?: string | null; contactPoints?: ContactPoint[] | null }>,
  contacts: Array<{
    clientContactFirstName?: string | null;
    clientContactLastName?: string | null;
    contactPoints?: ContactPoint[] | null;
  }>,
): AddressBookSuggestionEntry[] {
  const seen = new Set<string>();
  const out: AddressBookSuggestionEntry[] = [];

  const add = (emailRaw: string, name?: string) => {
    const email = normalizeEmail(emailRaw);
    if (!email) return;
    const key = email.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ email, name: name || null });
  };

  for (const org of organizations) {
    const orgName = org.clientName?.trim();
    for (const point of sortContactPoints(org.contactPoints ?? [], 'EMAIL')) {
      add(point.value, pickDisplayName(point.contactName, orgName));
    }
  }

  for (const contact of contacts) {
    const personName = [contact.clientContactFirstName, contact.clientContactLastName]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(' ');
    for (const point of sortContactPoints(contact.contactPoints ?? [], 'EMAIL')) {
      add(point.value, pickDisplayName(point.contactName, personName));
    }
  }

  return out;
}

/** Collect unique email addresses from organization + people contact points. */
export function collectAddressBookSuggestionEmails(
  organizations: Array<{ contactPoints?: ContactPoint[] | null }>,
  contacts: Array<{ contactPoints?: ContactPoint[] | null }>,
): string[] {
  return collectAddressBookSuggestions(organizations, contacts).map((entry) => entry.email);
}

/**
 * Build To/Cc/Bcc autocomplete list from sent history and/or address book,
 * preserving MRU order first, then address book emails not already included.
 */
export function buildComposeRecipientSuggestions(options: {
  includeSentHistoryEmails: boolean;
  includeAddressBookEmails: boolean;
  recentRecipients?: RecentRecipientSuggestionInput[];
  addressBookEntries?: AddressBookSuggestionEntry[];
  /** @deprecated Prefer recentRecipients */
  sentHistoryEmails?: string[];
  /** @deprecated Prefer addressBookEntries */
  addressBookEmails?: string[];
}): ComposeRecipientSuggestion[] {
  const seen = new Set<string>();
  const out: ComposeRecipientSuggestion[] = [];

  if (options.includeSentHistoryEmails) {
    const recent: RecentRecipientSuggestionInput[] =
      options.recentRecipients ??
      (options.sentHistoryEmails ?? []).map((email) => ({ email }));
    for (const item of recent) {
      const email = normalizeEmail(item.email);
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        email,
        name: item.name ?? null,
        source: 'recent',
        lastUsedAt: item.lastUsedAt,
      });
    }
  }

  if (options.includeAddressBookEmails) {
    const book: AddressBookSuggestionEntry[] =
      options.addressBookEntries ??
      (options.addressBookEmails ?? []).map((email) => ({ email }));
    for (const item of book) {
      const email = normalizeEmail(item.email);
      if (!email) continue;
      const key = email.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push({
        email,
        name: item.name ?? null,
        source: 'addressBook',
      });
    }
  }

  return out;
}

function matchesQuery(item: ComposeRecipientSuggestion, queryLower: string): boolean {
  if (!queryLower) return true;
  if (item.email.toLowerCase().includes(queryLower)) return true;
  const name = item.name?.trim().toLowerCase();
  return Boolean(name && name.includes(queryLower));
}

/**
 * Filter suggestions for the open typeahead.
 * Empty query → top recent only. Typed query → Recent + Address book sections.
 */
export function filterComposeRecipientSuggestions(
  items: ComposeRecipientSuggestion[],
  query: string,
  excludeEmails: readonly string[] = [],
  options?: {
    emptyQueryRecentLimit?: number;
    maxPerSection?: number;
    maxTotal?: number;
  },
): FilteredComposeRecipientSuggestions {
  const emptyLimit = options?.emptyQueryRecentLimit ?? EMPTY_QUERY_RECENT_LIMIT;
  const maxPerSection = options?.maxPerSection ?? MAX_PER_SECTION;
  const maxTotal = options?.maxTotal ?? MAX_TOTAL;
  const exclude = new Set(
    excludeEmails.map((email) => email.trim().toLowerCase()).filter(Boolean),
  );
  const queryLower = query.trim().toLowerCase();

  const available = items.filter((item) => !exclude.has(item.email.toLowerCase()));

  if (!queryLower) {
    const recent = available.filter((item) => item.source === 'recent').slice(0, emptyLimit);
    const addressBook = available
      .filter((item) => item.source === 'addressBook')
      .slice(0, emptyLimit);
    const flat = [...recent, ...addressBook].slice(0, maxTotal);
    return {
      recent: flat.filter((item) => item.source === 'recent'),
      addressBook: flat.filter((item) => item.source === 'addressBook'),
      flat,
    };
  }

  const recent: ComposeRecipientSuggestion[] = [];
  const addressBook: ComposeRecipientSuggestion[] = [];
  for (const item of available) {
    if (!matchesQuery(item, queryLower)) continue;
    if (item.source === 'recent') {
      if (recent.length < maxPerSection) recent.push(item);
    } else if (addressBook.length < maxPerSection) {
      addressBook.push(item);
    }
    if (recent.length + addressBook.length >= maxTotal) break;
  }

  // Prefer filling remaining slots from the other section if one side is short.
  const flat = [...recent, ...addressBook].slice(0, maxTotal);
  return {
    recent: flat.filter((item) => item.source === 'recent'),
    addressBook: flat.filter((item) => item.source === 'addressBook'),
    flat,
  };
}

/** Section header label for the typeahead list. */
export function suggestionSectionLabel(source: ComposeRecipientSuggestionSource): string {
  return source === 'recent' ? 'Recent' : 'Address book';
}
